import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, Phone, MapPin, Clock } from "lucide-react";
import { NAV } from "@/data/nav";
import { SITE } from "@/data/site";
import { MegaPanel } from "./MegaMenu";
import { MobileMenu } from "./MobileMenu";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const TopBar = () => (
  <div className="bg-[#111] text-[13px] text-zinc-300">
    <div className="wrap flex h-10 items-center justify-between gap-4">
      <p className="hidden truncate md:block">
        <span className="font-semibold text-[#F2C200]">Wholesale pricing</span> · Professional installation · Contractor supply &amp; delivery
      </p>
      <div className="flex w-full items-center justify-between gap-5 md:w-auto md:justify-end">
        <a href={SITE.mapsUrl} target="_blank" rel="noopener noreferrer" className="hidden items-center gap-1.5 hover:text-white xl:inline-flex" data-testid="topbar-directions-link">
          <MapPin className="h-3.5 w-3.5 text-[#F2C200]" aria-hidden="true" /> {SITE.street}, {SITE.city}
        </a>
        <span className="hidden items-center gap-1.5 xl:inline-flex">
          <Clock className="h-3.5 w-3.5 text-[#F2C200]" aria-hidden="true" /> Mon–Fri from 9 AM
        </span>
        <Link to="/contractor-pricing" className="font-semibold text-white underline-offset-4 hover:underline" data-testid="topbar-contractor-pricing-link">
          Contractor Pricing
        </Link>
        <a href={`tel:${SITE.tel}`} className="inline-flex items-center gap-1.5 font-bold text-[#F2C200]" data-testid="topbar-phone-link">
          <Phone className="h-3.5 w-3.5" aria-hidden="true" /> {SITE.phone}
        </a>
      </div>
    </div>
  </div>
);

export const Header = () => {
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(null);
    setMobile(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <header className="sticky top-0 z-50" onMouseLeave={() => setOpen(null)} data-testid="site-header">
      <TopBar />
      <div className={`relative border-b border-[#ebe7de] bg-white/95 backdrop-blur-xl transition-shadow duration-300 ${scrolled ? "shadow-[0_12px_30px_-20px_rgba(0,0,0,0.35)]" : ""}`}>
        <div className="wrap flex h-[76px] items-center gap-4">
          <Link to="/" className="shrink-0" aria-label="Wholesale LVP Flooring home" data-testid="header-logo-link">
            <img src="/img/logo-360.webp" srcSet="/img/logo-360.webp 360w, /img/logo.webp 640w" sizes="(min-width:1024px) 210px, 168px" alt="Wholesale LVP Flooring" width="640" height="122" fetchPriority="high" className="h-7 w-auto sm:h-8 lg:h-10" />
          </Link>
          <div className="ml-auto flex items-center gap-2 lg:gap-4">
            <a href={`tel:${SITE.tel}`} className="hidden items-center gap-2 px-2 text-lg font-extrabold tracking-tight md:inline-flex" data-testid="header-phone-link">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-[#111] text-[#F2C200]"><Phone className="h-4 w-4" aria-hidden="true" /></span> {SITE.phone}
            </a>
            <Link to="/free-estimate" className="btn btn-yellow hidden h-11 px-5 sm:inline-flex" data-testid="header-free-estimate-button">
              Free Estimate
            </Link>
            <a href={`tel:${SITE.tel}`} className="grid h-11 w-11 place-items-center rounded-full bg-[#111] text-[#F2C200] sm:hidden" aria-label={`Call ${SITE.phone}`} data-testid="header-mobile-call-button">
              <Phone className="h-5 w-5" />
            </a>
            <button
              type="button"
              className="grid h-11 w-11 place-items-center rounded-full border border-[#ddd] lg:hidden"
              aria-label="Open menu"
              onClick={() => setMobile(true)}
              data-testid="mobile-menu-toggle"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
        <nav aria-label="Main" className="hidden border-t border-[#ebe7de] lg:block">
          <div className="wrap">
            <ul className="-ml-2.5 flex items-center justify-between">
              {NAV.map((item) =>
                item.mega ? (
                  <li key={item.label} onMouseEnter={() => setOpen(item.mega)}>
                    <button
                      type="button"
                      className={`navlink ${pathname.startsWith(item.href) ? "active" : ""}`}
                      aria-expanded={open === item.mega}
                      onClick={() => setOpen(open === item.mega ? null : item.mega)}
                      data-testid={`mega-menu-trigger-${item.mega}`}
                    >
                      {item.label}
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open === item.mega ? "rotate-180" : ""}`} aria-hidden="true" />
                    </button>
                  </li>
                ) : (
                  <li key={item.label} onMouseEnter={() => setOpen(null)}>
                    <NavLink to={item.href} className="navlink" data-testid={`nav-${slug(item.label)}`}>
                      {item.label}
                    </NavLink>
                  </li>
                )
              )}
            </ul>
          </div>
        </nav>
        <AnimatePresence>{open && <MegaPanel key={open} which={open} />}</AnimatePresence>
      </div>
      <AnimatePresence>{mobile && <MobileMenu onClose={() => setMobile(false)} />}</AnimatePresence>
    </header>
  );
};
